import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import * as signalR from '@microsoft/signalr';
import { AuthService } from './auth.service';
import { environment } from '../../../../environments/environment';

export const SIGNALR_EVENTS = {
    PORTAL_ADMIN_USER_DISABLED: 'PortalAdminUserDisabled',
    PERMISSION_UPDATED: 'PermissionUpdated',
    FORCE_LOGOUT: 'ForceLogout',
    SESSION_EXPIRED: 'SessionExpired'
} as const;

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {

    private hubConnection: signalR.HubConnection | null = null;

    private readonly apiUrl = `${environment.apiGatewayBaseUrl}`;
    // private readonly apiUrl = `https://localhost:7206`;

    constructor(
        private authService: AuthService,
        private router: Router,
        private toastr: ToastrService
    ) { }

    // ─────────────────────────────────────────
    // Call this right after login success
    // ─────────────────────────────────────────
    startConnection(): void {
        const token = this.authService.getToken();
        if (!token) return;

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${this.apiUrl}/ws-hubs/portal-admin-user`, {
                accessTokenFactory: () => token  // sends JWT so backend identifies the user
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        this.hubConnection
            .start()
            .then(() => {
                // console.log('SignalR connected.');
                this.registerEvents();
            })
            .catch(err => console.error('SignalR connection error:', err));
    }

    // ─────────────────────────────────────────
    // Register all real-time events here
    // ─────────────────────────────────────────
    private registerEvents(): void {
        if (!this.hubConnection) return;

        // Fired when admin disables this user
        this.hubConnection.on(SIGNALR_EVENTS.PORTAL_ADMIN_USER_DISABLED, () => {
            this.toastr.error(
                'Your account has been disabled. Please contact the System Administrator.',
                'Account Disabled',
                { timeOut: 5000, disableTimeOut: false }
            );

            setTimeout(() => {
                this.authService.logout();
                this.router.navigate(['/login']);
            }, 1000);  // small delay so toastr is visible before redirect
        });
    }

    // ─────────────────────────────────────────
    // Call this on manual logout
    // ─────────────────────────────────────────
    stopConnection(): void {
        this.hubConnection
            ?.stop()
            .catch(err => console.error('SignalR stop error:', err));
        this.hubConnection = null;
    }

    ngOnDestroy(): void {
        this.stopConnection();
    }
}